/**
 * In-app help guides registry (AUTONOMY · Layer 3).
 *
 * Central manifest (CatalogSource pattern): guides are registered here and a
 * completeness test (`__tests__/registry.test.ts`) ensures that:
 *  - keys/slugs/section-ids are unique and non-empty,
 *  - EVERY navigable area of the app (`AREA_HELP`) points to an existing
 *    guide+section, and
 *  - every feature route under `routes/_app` belongs to an area with help.
 *
 * Adding a new feature ⇒ either its area is added here (with guide/section)
 * or the test fails: no screen without documentation.
 */
import { adminGuide } from "./guides/admin-guide";
import { teacherGuide } from "./guides/teacher-guide";
import { financeGuide } from "./guides/finance-guide";
import { crmGuide } from "./guides/crm-guide";
import { studentGuide } from "./guides/student-guide";
import { ioGuide } from "./guides/io-guide";
import { integrationsGuide } from "./guides/integrations-guide";
import { aiTutorGuide } from "./guides/ai-tutor-guide";
// B8 · AI authoring + academy assistant.
import { aiAuthoringGuide } from "./guides/ai-authoring-guide";
import type { HelpGuide, HelpTarget } from "./types";

/** All guides, in `/help` presentation order. */
export const HELP_GUIDES: HelpGuide[] = [
  adminGuide,
  teacherGuide,
  financeGuide,
  crmGuide,
  studentGuide,
  ioGuide,
  integrationsGuide,
  aiTutorGuide,
  aiAuthoringGuide,
];

export function findGuide(slug: string): HelpGuide | undefined {
  return HELP_GUIDES.find((g) => g.slug === slug);
}

/**
 * App-area → relevant help section map. The key is the first path segment
 * under `/u/$u` (e.g. `academy`, `finance`). Consumed by the contextual "?"
 * button (`HelpButton`) and by the completeness test.
 */
export const AREA_HELP: Readonly<Record<string, HelpTarget>> = {
  learn: { guide: "alumno", section: "aprender" },
  catalog: { guide: "alumno", section: "catalogo" },
  leaderboard: { guide: "alumno", section: "aprender" },
  certificates: { guide: "alumno", section: "certificados" },
  schedule: { guide: "alumno", section: "horario" },
  // 1C/1D: the student's classroom and their record.
  classroom: { guide: "alumno", section: "aula" },
  checkin: { guide: "alumno", section: "horario" },
  assignments: { guide: "alumno", section: "tareas" },
  "final-project": { guide: "alumno", section: "proyecto-final" },
  // B6 · levels: the student's path.
  path: { guide: "alumno", section: "mi-ruta" },
  payments: { guide: "alumno", section: "pagos" },
  notifications: { guide: "alumno", section: "comunicaciones" },
  invitations: { guide: "alumno", section: "personalizacion" },
  profile: { guide: "alumno", section: "personalizacion" },
  // §M1: "My profiles" — what I am in each academy and what each profile unlocks.
  profiles: { guide: "alumno", section: "mis-perfiles" },
  themes: { guide: "alumno", section: "personalizacion" },
  language: { guide: "alumno", section: "personalizacion" },
  live: { guide: "profesor", section: "clases-en-vivo" },
  authoring: { guide: "administrador", section: "academico" },
  academy: { guide: "administrador", section: "academico" },
  organizations: { guide: "administrador", section: "configuracion" },
  activity: { guide: "administrador", section: "trazabilidad" },
  trash: { guide: "administrador", section: "trazabilidad" },
  finance: { guide: "caja", section: "registrar-pagos" },
  crm: { guide: "captacion", section: "pipeline" },
  "ai-tutor": { guide: "tutor-ia", section: "chat" },
  admin: { guide: "administrador", section: "features" },
  help: { guide: "administrador", section: "features" },
  // Sub-areas with their own guide (those screens' "?" points more precisely).
  "admin.import-export": { guide: "importar", section: "panorama" },
  "admin.integrations": { guide: "integraciones", section: "panorama" },
  // B8 · the assistant panel and AI authoring have their own guide.
  "admin.academy-bot": { guide: "autoria-ia", section: "asistente" },
  "admin.billing": { guide: "caja", section: "configuracion" },
  "academy.cohorts": { guide: "administrador", section: "academico" },
  "academy.tutoring": { guide: "profesor", section: "tutorias" },
  // B1: the non-payment policy is a CASHIER decision (whoever collects decides it).
  "academy.entitlement": { guide: "caja", section: "titularidad" },
  "academy.campuses": { guide: "administrador", section: "modalidad" },
  "finance.rates": { guide: "caja", section: "tasa" },
};

/** Resolves an area's help target (undefined if not mapped). */
export function helpTargetForArea(area: string): HelpTarget | undefined {
  return AREA_HELP[area];
}

/**
 * §M2 · A section's summary (a screen's "explanatory text").
 * It's the SAME prose seen in the guide: the screen doesn't rewrite its own
 * explanation, it takes it from here.
 */
export function helpSummaryFor(target: HelpTarget) {
  return findGuide(target.guide)?.sections.find((s) => s.id === target.section)?.summary;
}

/**
 * Anti-drift validation of the registry (mirror of `FeatureCatalog::validate`).
 * Returns the list of problems; empty = healthy registry.
 *
 * The messages are diagnostics FOR DEVELOPERS (they fail the completeness
 * test) — they never reach the UI, which is why they aren't i18n'd.
 */
/* oxlint-disable lingui/no-unlocalized-strings */
export function validateHelpRegistry(): string[] {
  const issues: string[] = [];
  const slugs = new Set<string>();
  const keys = new Set<string>();
  for (const guide of HELP_GUIDES) {
    if (!/^help\.[a-z_]+$/.test(guide.key)) {
      issues.push(`guide key inválida: ${guide.key}`);
    }
    if (keys.has(guide.key)) {
      issues.push(`guide key duplicada: ${guide.key}`);
    }
    keys.add(guide.key);
    if (!/^[a-z][a-z-]*$/.test(guide.slug)) {
      issues.push(`guide slug inválido: ${guide.slug}`);
    }
    if (slugs.has(guide.slug)) {
      issues.push(`guide slug duplicado: ${guide.slug}`);
    }
    slugs.add(guide.slug);
    if (guide.sections.length === 0) {
      issues.push(`guía sin secciones: ${guide.key}`);
    }
    const sectionIds = new Set<string>();
    for (const section of guide.sections) {
      if (!/^[a-z][a-z-]*$/.test(section.id)) {
        issues.push(`section id inválido: ${guide.key}#${section.id}`);
      }
      if (sectionIds.has(section.id)) {
        issues.push(`section id duplicado: ${guide.key}#${section.id}`);
      }
      sectionIds.add(section.id);
    }
  }
  for (const [area, target] of Object.entries(AREA_HELP)) {
    const guide = findGuide(target.guide);
    if (!guide) {
      issues.push(`área ${area}: guía inexistente ${target.guide}`);
    } else if (!guide.sections.some((s) => s.id === target.section)) {
      issues.push(`área ${area}: sección inexistente ${target.guide}#${target.section}`);
    }
  }
  return issues;
}
/* oxlint-enable lingui/no-unlocalized-strings */
